import { useEffect, useState } from "react";
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudLightning, Wind, Waves, Thermometer } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/api";

const ICONS = { clear: Sun, partly: CloudSun, cloudy: Cloud, fog: CloudFog, rain: CloudRain, storm: CloudLightning };

function codeToKind(c) {
  if (c === 0) return "clear";
  if (c === 1 || c === 2) return "partly";
  if (c === 3) return "cloudy";
  if (c === 45 || c === 48) return "fog";
  if (c >= 95) return "storm";
  return "rain";
}

export default function WeatherWidget({ compact = false }) {
  const { lang, t } = useLang();
  const [wx, setWx] = useState(null);
  const [sea, setSea] = useState(null);
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    api.get("/weather")
      .then((r) => {
        setWx(r.data.current || null);
        setSea(r.data.sea ?? null);
        setForecast((r.data.daily || []).map((d) => ({
          ...d,
          label: new Date(d.date + "T12:00").toLocaleDateString(lang, { weekday: "short" }),
        })));
      })
      .catch(() => {});
  }, [lang]);

  if (!wx) return null;
  const kind = codeToKind(wx.weather_code);
  const Icon = ICONS[kind];

  if (compact) {
    return (
      <div data-testid="weather-compact"
        className="inline-flex flex-wrap justify-center items-center gap-x-5 gap-y-2 rounded-full bg-white/75 backdrop-blur border border-black/5 px-5 py-2.5 shadow-sm">
        <span className="inline-flex items-center gap-2 text-grafite font-heading font-semibold text-lg" data-testid="weather-compact-temp">
          <Icon className="w-5 h-5 text-terracotta" /> {Math.round(wx.temperature_2m)}°
        </span>
        <span className="text-xs font-semibold text-grafite/60 inline-flex items-center gap-1.5">
          <Wind className="w-3.5 h-3.5 text-turchese" /> {Math.round(wx.wind_speed_10m)} km/h
        </span>
        {sea != null && (
          <span className="text-xs font-semibold text-grafite/60 inline-flex items-center gap-1.5" data-testid="weather-compact-sea">
            <Waves className="w-3.5 h-3.5 text-mare" /> {t("weather.sea")} {Math.round(sea * 10) / 10}°C
          </span>
        )}
      </div>
    );
  }

  return (
    <div data-testid="weather-widget"
      className="mb-10 rounded-3xl bg-gradient-to-br from-turchese to-mare text-white p-6 sm:p-8 shadow-[0_16px_50px_rgba(14,110,140,0.25)]">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-5">
        <div className="flex items-center gap-4">
          <Icon className="w-12 h-12" strokeWidth={1.5} />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">{t("weather.title")}</p>
            <p className="font-heading font-bold text-4xl leading-none mt-1" data-testid="weather-temp">
              {Math.round(wx.temperature_2m)}°
            </p>
          </div>
        </div>
        <p className="font-accent text-2xl text-sabbia">{t(`weather.${kind}`)}</p>
        <div className="flex items-center gap-2.5" data-testid="weather-wind">
          <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><Wind className="w-5 h-5" /></span>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-white/70">{t("weather.wind")}</p>
            <p className="font-heading font-semibold text-lg leading-none">{Math.round(wx.wind_speed_10m)} km/h</p>
          </div>
        </div>
        {sea != null && (
          <div className="flex items-center gap-2.5" data-testid="weather-sea">
            <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><Waves className="w-5 h-5" /></span>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-white/70">{t("weather.sea")}</p>
              <p className="font-heading font-semibold text-lg leading-none">{Math.round(sea * 10) / 10}°C</p>
            </div>
          </div>
        )}
        <p className="ml-auto text-[10px] uppercase tracking-wider text-white/50 inline-flex items-center gap-1">
          <Thermometer className="w-3 h-3" /> {t("weather.live")}
        </p>
      </div>
      {forecast.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/20 grid grid-cols-5 gap-2" data-testid="weather-forecast">
          {forecast.map((d) => {
            const DayIcon = ICONS[codeToKind(d.code)];
            return (
              <div key={d.date} className="text-center rounded-2xl bg-white/10 py-3 px-1" data-testid={`forecast-${d.date}`}>
                <p className="text-[10px] uppercase font-bold text-white/70">{d.label}</p>
                <DayIcon className="w-6 h-6 mx-auto my-1.5" strokeWidth={1.5} />
                <p className="text-xs font-bold">{Math.round(d.max)}° <span className="font-normal text-white/60">{Math.round(d.min)}°</span></p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
